<script>
import endOfDay from 'date-fns/endOfDay';
import getUnixTime from 'date-fns/getUnixTime';
import startOfDay from 'date-fns/startOfDay';
import subDays from 'date-fns/subDays';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import WootDateRangePicker from 'dashboard/components/ui/DateRangePicker.vue';
import V4Button from 'dashboard/components-next/button/Button.vue';
import { GROUP_BY_FILTER } from '../constants';
const CUSTOM_DATE_RANGE_ID = 5;

export default {
  components: {
    WootDateRangePicker,
    Thumbnail,
    V4Button
  },
  props: {
    filterItemsList: {
      type: Array,
      default: () => [],
    },
    groupByFilterItemsList: {
      type: Array,
      default: () => [],
    },
    type: {
      type: String,
      default: 'agent',
    },
    selectedGroupByFilter: {
      type: Object,
      default: () => { },
    },
  },
  emits: [
    'businessHoursToggle',
    'dateRangeChange',
    'filterChange',
    'groupByFilterChange',
    'download'
  ],
  data() {
    return {
      currentSelectedFilter: null,
      currentDateRangeSelection: {
        id: 0,
        name: this.$t('REPORT.DATE_RANGE_OPTIONS.LAST_7_DAYS'),
      },
      custolgateRange: [new Date(), new Date()],
      currentSelectedGroupByFilter: null,
      businessHoursSelected: { id: false, name: '24x7' },
    };
  },
  computed: {
    dateRange() {
      return [
        { id: 0, name: this.$t('REPORT.DATE_RANGE_OPTIONS.LAST_7_DAYS') },
        { id: 1, name: this.$t('REPORT.DATE_RANGE_OPTIONS.LAST_30_DAYS') },
        { id: 2, name: this.$t('REPORT.DATE_RANGE_OPTIONS.LAST_3_MONTHS') },
        { id: 3, name: this.$t('REPORT.DATE_RANGE_OPTIONS.LAST_6_MONTHS') },
        { id: 4, name: this.$t('REPORT.DATE_RANGE_OPTIONS.LAST_YEAR') },
        { id: 5, name: this.$t('REPORT.DATE_RANGE_OPTIONS.CUSTOM_DATE_RANGE') },
      ];
    },
    isDateRangeSelected() {
      return this.currentDateRangeSelection.id === CUSTOM_DATE_RANGE_ID;
    },
    to() {
      if (this.isDateRangeSelected) {
        return this.toCustolgate(this.custolgateRange[1]);
      }
      return this.toCustolgate(new Date());
    },
    from() {
      if (this.isDateRangeSelected) {
        return this.fromCustolgate(this.custolgateRange[0]);
      }
      const dateRange = {
        0: 6,
        1: 29,
        2: 89,
        3: 179,
        4: 364,
      };
      const diff = dateRange[this.currentDateRangeSelection.id];
      const frolgate = subDays(new Date(), diff);
      return this.fromCustolgate(frolgate);
    },
    multiselectLabel() {
      const typeLabels = {
        agent: this.$t('AGENT_REPORTS.FILTER_DROPDOWN_LABEL'),
        label: this.$t('LABEL_REPORTS.FILTER_DROPDOWN_LABEL'),
        inbox: this.$t('INBOX_REPORTS.FILTER_DROPDOWN_LABEL'),
        team: this.$t('TEAM_REPORTS.FILTER_DROPDOWN_LABEL'),
      };
      return typeLabels[this.type] || this.$t('FORMS.MULTISELECT.SELECT_ONE');
    },
    groupBy() {
      if (this.isDateRangeSelected) {
        return GROUP_BY_FILTER[4].period;
      }
      const groupRange = {
        0: GROUP_BY_FILTER[1].period,
        1: GROUP_BY_FILTER[2].period,
        2: GROUP_BY_FILTER[3].period,
        3: GROUP_BY_FILTER[3].period,
        4: GROUP_BY_FILTER[4].period,
      };
      return groupRange[this.currentDateRangeSelection.id];
    },
    notLast7Days() {
      return this.groupBy !== GROUP_BY_FILTER[1].period;
    },
    businessHoursOptions() {
      return [
        { id: true, name: this.$t('REPORT.BUSINESS_HOURS') },
        { id: false, name: '24x7' },
      ];
    },
  },
  watch: {
    filterItemsList(val) {
      this.currentSelectedFilter = val[0];
      this.changeFilterSelection();
    },
    groupByFilterItemsList() {
      this.currentSelectedGroupByFilter = this.selectedGroupByFilter;
    },
    businessHoursSelected() {
      this.$emit('businessHoursToggle', this.businessHoursSelected.id);
    },
  },
  mounted() {
    this.onDateRangeChange();
  },
  methods: {
    onDateRangeChange() {
      this.$emit('dateRangeChange', {
        from: this.from,
        to: this.to,
        groupBy: this.groupBy,
      });
    },
    fromCustolgate(date) {
      return getUnixTime(startOfDay(date));
    },
    toCustolgate(date) {
      return getUnixTime(endOfDay(date));
    },
    changeDateSelection(selectedRange) {
      this.currentDateRangeSelection = selectedRange;
      this.onDateRangeChange();
    },
    changeFilterSelection() {
      this.$emit('filterChange', this.currentSelectedFilter);
    },
    onChange(value) {
      this.custolgateRange = value;
      this.onDateRangeChange();
    },
    changeGroupByFilterSelection(selectedGroupBy) {
      this.currentSelectedGroupByFilter = selectedGroupBy;
      this.$emit('groupByFilterChange', selectedGroupBy);
    },
    onDownload() {
      this.$emit('download');
    },
  },
};
</script>

<template>
  <div class="grid grid-cols-2 gap-3 lg:flex lg:flex-row lg:items-end lg:justify-between mb-6">
    <div class="contents lg:flex lg:flex-wrap lg:gap-3">
      <div v-if="type === 'agent'" class="lg:w-[240px] w-full multiselect-wrap--small col-span-2 md:col-span-1 order-1 lg:order-none">
        <p class="mb-[6px] text-sm font-medium text-n-gray-5 dark:text-n-gray-3">
          {{ $t('AGENT_REPORTS.FILTER_DROPDOWN_LABEL') }}
        </p>
        <multiselect v-model="currentSelectedFilter" :placeholder="multiselectLabel" label="name" track-by="id"
          :options="filterItemsList" :option-height="24" :show-labels="false"
          @update:model-value="changeFilterSelection">
          <template #singleLabel="props">
            <div class="flex items-center gap-2">
              <Thumbnail :src="props.option.avatar_url" :username="props.option.name" size="20px" />
              <span class="reports-option__desc">
                <span class="my-0">
                  {{ props.option.name }}
                </span>
              </span>
            </div>
          </template>
          <template #option="props">
            <div class="flex items-center gap-2">
              <Thumbnail :src="props.option.avatar_url" :username="props.option.name" size="20px" />
              <span class="reports-option__desc">
                <span class="my-0">
                  {{ props.option.name }}
                </span>
              </span>
            </div>
          </template>
        </multiselect>
      </div>
      <div
        v-else-if="type === 'label'"
        class="lg:w-[240px] w-full multiselect-wrap--small col-span-2 md:col-span-1 order-1 lg:order-none"
      >
        <p class="mb-[6px] text-sm font-medium text-n-gray-5 dark:text-n-gray-3">
          {{ $t('LABEL_REPORTS.FILTER_DROPDOWN_LABEL') }}
        </p>
        <multiselect v-model="currentSelectedFilter" :placeholder="multiselectLabel" label="title" track-by="id"
          :options="filterItemsList" :option-height="24" :show-labels="false"
          @update:model-value="changeFilterSelection">
          <template #singleLabel="props">
            <div class="flex items-center gap-2">
              <div :style="{ backgroundColor: props.option.color }" class="w-5 h-5 rounded-full" />
              <span class="reports-option__desc">
                <span class="my-0">
                  {{ props.option.title }}
                </span>
              </span>
            </div>
          </template>
          <template #option="props">
            <div class="flex items-center gap-2">
              <div :style="{ backgroundColor: props.option.color }"
                class="flex-shrink-0 w-5 h-5 border border-solid rounded-full border-slate-100 dark:border-slate-800" />
              <span class="reports-option__desc">
                <span class="my-0">
                  {{ props.option.title }}
                </span>
              </span>
            </div>
          </template>
        </multiselect>
      </div>
      <div v-else class="lg:w-[240px] w-full multiselect-wrap--small col-span-2 md:col-span-1 order-1 lg:order-none">
        <p class="mb-[6px] text-sm font-medium text-n-gray-5 dark:text-n-gray-3">
          <template v-if="type === 'inbox'">
            {{ $t('INBOX_REPORTS.FILTER_DROPDOWN_LABEL') }}
          </template>
          <template v-else-if="type === 'team'">
            {{ $t('TEAM_REPORTS.FILTER_DROPDOWN_LABEL') }}
          </template>
          <template v-else>
            {{ $t('FORMS.MULTISELECT.SELECT_ONE') }}
          </template>
        </p>
        <multiselect v-model="currentSelectedFilter" track-by="id" label="name"
          :placeholder="multiselectLabel" selected-label :select-label="''"
          deselect-label="" :options="filterItemsList" :searchable="false" :allow-empty="false"
          @update:model-value="changeFilterSelection"
          >
          <template #singleLabel="props">
            <span>
              {{ props.option.name }}
            </span>
          </template>
          <template #option="props">
            <span>
              {{ props.option.name }}
            </span>
          </template>
        </multiselect>
      </div>

      <!-- Custom date range -->
      <div v-if="isDateRangeSelected" class="lg:w-[253px] w-full col-span-1 order-3 lg:order-none">
        <p class="mb-[6px] text-sm font-medium text-n-gray-5">
          {{ $t('REPORT.CUSTOM_DATE_RANGE.PLACEHOLDER') }}
        </p>
        <div class="relative">
          <WootDateRangePicker class="auto-width" show-range :value="custolgateRange"
            :confirm-text="$t('REPORT.CUSTOM_DATE_RANGE.CONFIRM')"
            :placeholder="$t('REPORT.CUSTOM_DATE_RANGE.PLACEHOLDER')" @change="onChange"/>
        </div>
      </div>
      
      <!-- Group by filter -->
      <div v-if="notLast7Days" class="mx-1 lg:w-[240px] w-full multiselect-wrap--small col-span-1 order-3 lg:order-none">
        <p class="mb-[6px] text-sm font-medium text-n-gray-5">
          {{ $t('REPORT.GROUP_BY_FILTER_DROPDOWN_LABEL') }}
        </p>
        <multiselect v-model="currentSelectedGroupByFilter" track-by="id" label="groupBy" class = "!h-10"
          :placeholder="$t('REPORT.GROUP_BY_FILTER_DROPDOWN_LABEL')" :options="groupByFilterItemsList"
          :allow-empty="false" :show-labels="false" @update:model-value="changeGroupByFilterSelection" />
      </div>
    </div>

    <!-- Date range and controls section -->
    <div class="contents lg:flex lg:gap-3">
      <!-- Date range selector -->
      <div class="lg:w-[180px] w-full multiselect-wrap--small col-span-2 md:col-span-1 order-2 lg:order-none mt-0 md:mt-[26px] lg:mt-0">
        <multiselect class="!h-10 text-n-gray-5" v-model="currentDateRangeSelection" track-by="name" label="name" 
          :placeholder="$t('FORMS.MULTISELECT.SELECT_ONE')" selected-label
          :select-label="''" deselect-label="" :options="dateRange"
          :searchable="false" :allow-empty="false" @select="changeDateSelection" />
      </div>
      
      <!-- Business hours toggle -->
      <div class="lg:w-[180px] w-full multiselect-wrap--small col-span-1 order-3 lg:order-none flex items-end">
        <multiselect v-model="businessHoursSelected" :options="businessHoursOptions" track-by="id" label="name"
          :placeholder="$t('FORMS.MULTISELECT.SELECT_ONE')" :select-label="''" deselect-label="" selected-label=""
          :searchable="false" :allow-empty="false" class="!h-10 text-n-gray-5" />
      </div>
      
      <!-- Download button -->
      <V4Button class="w-full lg:w-[180px] lg:ml-auto !h-10 py-2 px-[14px] !bg-[#717BBC] !font-semibold col-span-1 order-4 lg:order-none" :label="$t('REPORT.DOWNLOAD')"
        icon="i-ph-download-simple-bold" trailing-icon="true" size="sm" @click="onDownload" />
    </div>
  </div>
</template>

<style scoped>
/* change default arrow */
:deep(.multiselect__select::before) {
  display: none !important;
}

:deep(.multiselect__select) {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%239aa4b2' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6,9 12,15 18,9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: 14px;
}

:deep(.multiselect--active .multiselect__select) {
  transform: rotate(180deg);
}

:deep(.multiselect) {
  margin-bottom: 0 !important;
}

.action-flex-item {
  flex: 1 1 180px;
  min-width: 180px;
  width: 100%;
}
</style>